import React, {
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../context/SocketContext';
import './AvailableJobs.css';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  'http://localhost:5000/api';

const getCurrencySymbol = (code) => {
  const symbols = {
    USD: '$',
    EUR: '€',
    GBP: '£'
  };

  return symbols[code] || '$';
};

const formatNumber = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '0';
  }

  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0
  }).format(number);
};

const formatDate = (value) => {
  if (!value) {
    return 'Not specified';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Not specified';
  }

  return date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getPostedTime = (value, currentTime = Date.now()) => {
  if (!value) {
    return 'Recently';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Recently';
  }

  const difference = Math.max(
    0,
    currentTime - date.getTime()
  );

  const seconds = Math.floor(
    difference / 1000
  );

  if (seconds < 60) {
    return 'Just now';
  }

  const minutes = Math.floor(
    seconds / 60
  );

  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(
    minutes / 60
  );

  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.floor(
    hours / 24
  );

  if (days < 7) {
    return `${days}d ago`;
  }

  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `${weeks}w ago`;
  }

  return formatDate(value);
};

const getDaysLeft = (deadline) => {
  if (!deadline) {
    return 'No deadline';
  }

  const date = new Date(deadline);

  if (Number.isNaN(date.getTime())) {
    return 'No deadline';
  }

  const days = Math.ceil(
    (date.getTime() - Date.now()) /
      (1000 * 60 * 60 * 24)
  );

  if (days < 0) {
    return 'Expired';
  }

  if (days === 0) {
    return 'Due today';
  }

  if (days === 1) {
    return '1 day left';
  }

  return `${days} days left`;
};

const getBudgetDisplay = (job) => {
  const currency =
    job.currency || 'USD';

  const symbol =
    getCurrencySymbol(currency);

  if (
    job.budgetType === 'quotation' ||
    (!job.budget &&
      !job.budgetMin &&
      !job.budgetMax)
  ) {
    return 'Request for Quotation';
  }

  if (job.budgetType === 'hourly') {
    const rate =
      job.budgetMin ||
      job.budget ||
      job.budgetMax ||
      0;

    return `${symbol}${formatNumber(
      rate
    )}/hr`;
  }

  if (
    job.budgetMin &&
    job.budgetMax
  ) {
    return `${symbol}${formatNumber(
      job.budgetMin
    )} – ${symbol}${formatNumber(
      job.budgetMax
    )}`;
  }

  const amount =
    job.budget ||
    job.budgetMin ||
    job.budgetMax ||
    0;

  return `${symbol}${formatNumber(
    amount
  )}`;
};

const getBudgetLabel = (job) => {
  if (
    job.budgetType === 'quotation' ||
    (!job.budget &&
      !job.budgetMin &&
      !job.budgetMax)
  ) {
    return 'Quotation';
  }

  if (job.budgetType === 'hourly') {
    return 'Per Hour';
  }

  return 'Fixed Price';
};

const AvailableJobs = () => {
  const navigate = useNavigate();
  const socket = useSocket();

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] =
    useState('');

  const [selectedPort, setSelectedPort] =
    useState('all');

  const [
    selectedService,
    setSelectedService
  ] = useState('all');

  const [
    selectedPricing,
    setSelectedPricing
  ] = useState('all');

  const [sortBy, setSortBy] =
    useState('newest');

  const [currentTime, setCurrentTime] =
    useState(Date.now());

  const fetchJobs = useCallback(
    async (refresh = false) => {
      try {
        if (refresh) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError('');

        const token =
          localStorage.getItem(
            'marilink_token'
          );

        if (!token) {
          throw new Error(
            'Please login to view available jobs.'
          );
        }

        const response = await fetch(
          `${API_BASE}/rfqs`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            data.message ||
              'Failed to fetch available jobs.'
          );
        }

        const now = new Date();

        const openJobs = (
          data.rfqs || []
        ).filter((job) => {
          if (job.status !== 'open') {
            return false;
          }

          if (
            job.deadline &&
            new Date(job.deadline) < now
          ) {
            return false;
          }

          return true;
        });

        setJobs(openJobs);
        setCurrentTime(Date.now());
      } catch (fetchError) {
        console.error(
          'Available jobs error:',
          fetchError
        );

        setJobs([]);

        setError(
          fetchError.message ||
            'Unable to load jobs.'
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchJobs();

    if (!socket) {
      return undefined;
    }

    const handleNewRFQ = () => {
      fetchJobs(true);
    };

    socket.on(
      'new_rfq',
      handleNewRFQ
    );

    return () => {
      socket.off(
        'new_rfq',
        handleNewRFQ
      );
    };
  }, [socket, fetchJobs]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const ports = useMemo(() => {
    return [
      ...new Set(
        jobs
          .map((job) => job.port)
          .filter(Boolean)
      )
    ].sort();
  }, [jobs]);

  const services = useMemo(() => {
    return [
      ...new Set(
        jobs
          .map(
            (job) => job.serviceType
          )
          .filter(Boolean)
      )
    ].sort();
  }, [jobs]);

  const urgentJobs = useMemo(() => {
    return jobs.filter((job) => {
      if (!job.deadline) {
        return false;
      }

      const days = Math.ceil(
        (new Date(job.deadline) -
          new Date()) /
          (1000 * 60 * 60 * 24)
      );

      return days >= 0 && days <= 3;
    }).length;
  }, [jobs]);

  const privateJobs = useMemo(() => {
    return jobs.filter(
      (job) => job.isPrivate
    ).length;
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    const term =
      searchTerm
        .trim()
        .toLowerCase();

    const result = jobs.filter(
      (job) => {
        const searchable = [
          job.vesselName,
          job.serviceType,
          job.port,
          job.nextPort,
          job.description,
          job.classApproval
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        const matchesSearch =
          !term ||
          searchable.includes(term);

        const matchesPort =
          selectedPort === 'all' ||
          job.port === selectedPort;

        const matchesService =
          selectedService === 'all' ||
          job.serviceType ===
            selectedService;

        const matchesPricing =
          selectedPricing === 'all' ||
          job.budgetType ===
            selectedPricing;

        return (
          matchesSearch &&
          matchesPort &&
          matchesService &&
          matchesPricing
        );
      }
    );

    return [...result].sort(
      (a, b) => {
        if (sortBy === 'deadline') {
          const aDeadline =
            new Date(
              a.deadline || 8640000000000000
            ).getTime();

          const bDeadline =
            new Date(
              b.deadline || 8640000000000000
            ).getTime();

          return aDeadline - bDeadline;
        }

        if (sortBy === 'budget') {
          const aBudget =
            Number(
              a.budgetMax ||
                a.budget ||
                a.budgetMin ||
                0
            );

          const bBudget =
            Number(
              b.budgetMax ||
                b.budget ||
                b.budgetMin ||
                0
            );

          return bBudget - aBudget;
        }

        return (
          new Date(
            b.createdAt || 0
          ).getTime() -
          new Date(
            a.createdAt || 0
          ).getTime()
        );
      }
    );
  }, [
    jobs,
    searchTerm,
    selectedPort,
    selectedService,
    selectedPricing,
    sortBy
  ]);

  const hasFilters =
    searchTerm ||
    selectedPort !== 'all' ||
    selectedService !== 'all' ||
    selectedPricing !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedPort('all');
    setSelectedService('all');
    setSelectedPricing('all');
    setSortBy('newest');
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        Loading available jobs...
      </div>
    );
  }

  return (
    <div className="available-jobs-page-minimal">
      <div className="page-header-minimal">
        <div className="hero-content">
          <h1>Available Jobs</h1>
          <p>
            Browse open service requests
            from vessel owners
          </p>
        </div>

        <button
          type="button"
          className="refresh-btn-minimal"
          disabled={refreshing}
          onClick={() =>
            fetchJobs(true)
          }
        >
          <i
            className={`fas fa-sync-alt ${
              refreshing ? 'fa-spin' : ''
            }`}
          ></i>
          Refresh
        </button>
      </div>

      <div className="available-jobs-layout">
        <aside className="jobs-filter-sidebar">
          <div className="filter-sidebar-heading">
            <div>
              <i className="fas fa-sliders-h"></i>
              <span>Filters</span>
            </div>

            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
              >
                Clear
              </button>
            )}
          </div>

          <div className="sidebar-filter-group">
            <label>
              <i className="fas fa-map-marker-alt"></i>
              Port
            </label>

            <div className="filter-select-wrapper">
              <select
                className="filter-select-minimal"
                value={selectedPort}
                onChange={(event) =>
                  setSelectedPort(
                    event.target.value
                  )
                }
              >
                <option value="all">
                  All Ports
                </option>

                {ports.map((port) => (
                  <option
                    key={port}
                    value={port}
                  >
                    {port}
                  </option>
                ))}
              </select>

              <i className="fas fa-chevron-down filter-arrow"></i>
            </div>
          </div>

          <div className="sidebar-filter-group">
            <label>
              <i className="fas fa-cogs"></i>
              Service
            </label>

            <div className="filter-select-wrapper">
              <select
                className="filter-select-minimal"
                value={selectedService}
                onChange={(event) =>
                  setSelectedService(
                    event.target.value
                  )
                }
              >
                <option value="all">
                  All Services
                </option>

                {services.map(
                  (service) => (
                    <option
                      key={service}
                      value={service}
                    >
                      {service}
                    </option>
                  )
                )}
              </select>

              <i className="fas fa-chevron-down filter-arrow"></i>
            </div>
          </div>

          <div className="sidebar-filter-group">
            <label>
              <i className="fas fa-wallet"></i>
              Pricing
            </label>

            <div className="filter-select-wrapper">
              <select
                className="filter-select-minimal"
                value={selectedPricing}
                onChange={(event) =>
                  setSelectedPricing(
                    event.target.value
                  )
                }
              >
                <option value="all">
                  All Pricing
                </option>

                <option value="fixed">
                  Fixed Price
                </option>

                <option value="hourly">
                  Hourly
                </option>

                <option value="quotation">
                  Quotation
                </option>
              </select>

              <i className="fas fa-chevron-down filter-arrow"></i>
            </div>
          </div>

          <div className="sidebar-filter-group">
            <label>
              <i className="fas fa-sort-amount-down"></i>
              Sort By
            </label>

            <div className="filter-select-wrapper">
              <select
                className="filter-select-minimal"
                value={sortBy}
                onChange={(event) =>
                  setSortBy(
                    event.target.value
                  )
                }
              >
                <option value="newest">
                  Newest First
                </option>

                <option value="deadline">
                  Deadline Soonest
                </option>

                <option value="budget">
                  Highest Budget
                </option>
              </select>

              <i className="fas fa-chevron-down filter-arrow"></i>
            </div>
          </div>

          <div className="jobs-sidebar-summary">
            <div>
              <span>Open Jobs</span>
              <strong>{jobs.length}</strong>
            </div>

            <div>
              <span>Urgent</span>
              <strong>
                {urgentJobs}
              </strong>
            </div>

            <div>
              <span>Private</span>
              <strong>
                {privateJobs}
              </strong>
            </div>
          </div>
        </aside>

        <main className="jobs-main-area">
          <div className="jobs-search-row">
            <div className="search-pill-minimal">
              <i className="fas fa-search"></i>

              <input
                type="search"
                placeholder="Search vessel, service, port..."
                value={searchTerm}
                onChange={(event) =>
                  setSearchTerm(
                    event.target.value
                  )
                }
              />

              {searchTerm && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() =>
                    setSearchTerm('')
                  }
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>

            <span className="results-count-minimal">
              {filteredJobs.length}{' '}
              {filteredJobs.length === 1
                ? 'Job'
                : 'Jobs'}
            </span>
          </div>

          {error ? (
            <div className="error-banner-minimal">
              <div>
                <i className="fas fa-exclamation-circle"></i>
                <span>{error}</span>
              </div>

              <button
                type="button"
                onClick={() =>
                  fetchJobs()
                }
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="jobs-grid-minimal">
              {filteredJobs.map(
                (job) => {
                  const daysLeft =
                    getDaysLeft(
                      job.deadline
                    );

                  const attachmentCount =
                    Array.isArray(
                      job.attachments
                    )
                      ? job.attachments
                          .length
                      : 0;

                  return (
                    <article
                      key={job._id}
                      className="job-card-minimal"
                    >
                      <div className="job-card-header">
                        <div className="job-badges">
                          <span className="job-id">
                            #
                            {job._id
                              ?.slice(-8)
                              .toUpperCase()}
                          </span>

                          <span className="job-service-type">
                            {
                              job.serviceType
                            }
                          </span>

                          {job.classApproval && (
                            <span className="job-class">
                              {
                                job.classApproval
                              }
                            </span>
                          )}

                          {job.isPrivate && (
                            <span className="job-private-badge">
                              <i className="fas fa-lock"></i>
                              Private
                            </span>
                          )}
                        </div>

                        <div className="job-deadline">
                          <i className="far fa-clock"></i>
                          {daysLeft}
                        </div>
                      </div>

                      <div className="job-title-row">
                        <div className="job-title-content">
                          <h3 className="job-vessel">
                            {job.vesselName ||
                              'Unnamed Vessel'}
                          </h3>

                          <div className="job-location">
                            <span>
                              <i className="fas fa-map-marker-alt"></i>
                              {job.port ||
                                'Port not specified'}
                            </span>

                            {job.nextPort && (
                              <>
                                <i className="fas fa-long-arrow-alt-right route-arrow"></i>

                                <span>
                                  <i className="fas fa-anchor"></i>
                                  {
                                    job.nextPort
                                  }
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="job-budget-compact">
                          <span>
                            {getBudgetLabel(
                              job
                            )}
                          </span>

                          <strong>
                            {getBudgetDisplay(
                              job
                            )}
                          </strong>
                        </div>
                      </div>

                      <p className="job-description">
                        {job.description ||
                          'No job description provided.'}
                      </p>

                      <div className="job-extra-info">
                        <div>
                          <i className="fas fa-calendar-alt"></i>

                          <span>
                            <small>
                              Deadline
                            </small>

                            <strong>
                              {formatDate(
                                job.deadline
                              )}
                            </strong>
                          </span>
                        </div>

                        <div>
                          <i className="fas fa-weight-hanging"></i>

                          <span>
                            <small>
                              GRT
                            </small>

                            <strong>
                              {job.grt
                                ? formatNumber(
                                    job.grt
                                  )
                                : 'N/A'}
                            </strong>
                          </span>
                        </div>

                        <div>
                          <i className="fas fa-certificate"></i>

                          <span>
                            <small>
                              Class
                            </small>

                            <strong>
                              {job.classApproval ||
                                'N/A'}
                            </strong>
                          </span>
                        </div>

                        <div>
                          <i className="fas fa-paperclip"></i>

                          <span>
                            <small>
                              Files
                            </small>

                            <strong>
                              {
                                attachmentCount
                              }
                            </strong>
                          </span>
                        </div>

                        <div>
                          <i className="far fa-clock"></i>

                          <span>
                            <small>
                              Posted
                            </small>

                            <strong>
                              {getPostedTime(
                                job.createdAt,
                                currentTime
                              )}
                            </strong>
                          </span>
                        </div>
                      </div>

                      <div className="job-footer-minimal">
                        <button
                          type="button"
                          className="btn-outline-minimal"
                          onClick={() =>
                            navigate(
                              `/service-provider/job/${job._id}`
                            )
                          }
                        >
                          <i className="fas fa-info-circle"></i>
                          Details
                        </button>

                        <button
                          type="button"
                          className="btn-primary-minimal"
                          onClick={() =>
                            navigate(
                              `/service-provider/submit-quotation/${job._id}`
                            )
                          }
                        >
                          <i className="fas fa-file-invoice"></i>
                          Submit Quote
                        </button>
                      </div>
                    </article>
                  );
                }
              )}

              {filteredJobs.length ===
                0 && (
                <div className="empty-state-minimal">
                  <i className="fas fa-inbox"></i>

                  <h3>
                    No jobs found
                  </h3>

                  <p>
                    Try adjusting your
                    search or filters.
                  </p>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AvailableJobs;